import {
  AmbientLight,
  Box3,
  BufferGeometry,
  Color,
  DirectionalLight,
  Line,
  LineBasicMaterial,
  LineDashedMaterial,
  Mesh,
  MeshPhongMaterial,
  MeshBasicMaterial,
  Object3D,
  Path,
  OrthographicCamera,
  DoubleSide,
  PMREMGenerator,
  sRGBEncoding,
  AxesHelper,
  RingGeometry,
  PerspectiveCamera,
  Raycaster,
  Vector2,
  PointLight,
  Scene,
  TextureLoader,
  Vector3,
  WebGLRenderer,
} from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { OrbitControls129 } from './source/OrbitControls129.js'; // 引入旧版本控制器，兼容ios13以下设备
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { isLowIOSDevice } from './utils/util';

import checkUtils from './utils/check';

import TWEEN from '@tweenjs/tween.js';

const { isString, isNumber, isVector3, isArray } = checkUtils;
const LineNumberOfPoints = 20;

// glTF texture types. `envMap` is deliberately omitted, as it's used internally
// by the loader but not part of the glTF format.
const MAP_NAMES = [
  'map',
  'aoMap',
  'emissiveMap',
  'glossinessMap',
  'metalnessMap',
  'normalMap',
  'roughnessMap',
  'specularMap',
];
const DEFAULT_CAMERA = '[default]';
const DEFAULT_FONT_STYLE = {
  color: 0xffffff,
  fontSize: 100,
  height: 1,
  font: 'https://mapapi.qq.com/web/dataLayerTest/fontJson/helvetiker_regular.typeface.json',
};
const DEFAULT_LIGHTS = [
  {
    type: 'DirectionalLight',
    id: 'default_direction_light',
    color: new Color(0xFFFFFF),
    intensity: 1,
    position: [0, 1, 0],
    target: [0, 0, 0],
  },
  {
    type: 'AmbientLight',
    id: 'default_ambient_light',
    color: new Color(0xFFFFFF),
    intensity: 1,
  },
  {
    type: 'PointLight',
    id: 'default_point_light',
    color: new Color(0xFFFFFF),
    intensity: 1,
    position: new Vector3(100, 100, 100),
  },
];
export default class ModelScene {
  constructor(container, sceneOptions = {}) {
    this.container = isString(container)
      ? document.getElementById(container)
      : container;
    this.lights = [];
    this.texts = [];
    this.lines = [];
    this.circles = [];
    this.rings = [];
    this.sceneOptions = sceneOptions;
    this.scene = new Scene();
    window.scene = this.scene;
    this.content = null;
    // 载入模型后是否自动调整模型和相机位置
    this.autoPosition = false;
    this.isOpenEnv = false;
    // 默认背景透明
    this.isTransparent = sceneOptions.isTransparent ||  true;
    this.hasControlDistanceChange = false;
    this.initRenderer();
    this.initCamera();
    this.addLights(DEFAULT_LIGHTS);
    this.animate = this.animate.bind(this);
    this.initOrbitControls();
    this.animate();
    // const loader = new GLTFLoader();
    // loader.load(sceneOptions.modelUrl,  (gltf)=> {
    //   console.log('modelLoaded')
    //   // 设置模型缩放比例
    //   gltf.scene.scale.set(0.01, 0.01, 0.01);
    //   // 相对X轴旋转弧度(为了让模型加载为合适的姿势)
    //   gltf.scene.rotation.x = -0.5 * Math.PI;
    //   gltf.scene.position.z = 1;
    //   console.log(gltf)
    //   // var box = new THREE.Box3().setFromObject( gltf.scene );
    //   // var size = box.size();
    //   // console.log(size)
    //   this.scene.add(gltf.scene);
    // });
    // const axesHelper = new AxesHelper(200);
    // this.scene.add(axesHelper);
    window.addEventListener('resize', this.onWindowResize.bind(this), false);
    // window.addEventListener('click', this.onMouseClick.bind(this));
  }

  // onMouseClick(e) {
  //   const raycaster = new Raycaster();
  //   const mouse = new Vector2();
  //   mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
  //   mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
  //   raycaster.setFromCamera(mouse, this.activeCamera);

  //   // 计算物体和射线的焦点
  //   const intersects = raycaster.intersectObjects(this.content.children);
  //   console.log('intersects', intersects);
  // }

  initRenderer() {
    this.renderer = new WebGLRenderer({ antialias: true, alpha: true });
    this.clearBackgroundColor(this.isTransparent);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.container.offsetWidth, this.container.offsetHeight);
    this.renderer.outputEncoding = sRGBEncoding;
    this.container.appendChild(this.renderer.domElement);
  }

  initCamera() {
    this.defaultCamera = new PerspectiveCamera(
      60,
      this.container.offsetWidth / this.container.offsetHeight,
      0.2,
      20000,
    );
    // this.defaultCamera = new OrthographicCamera( window.innerWidth / - 2,
    // window.innerWidth / 2, window.innerHeight / 2, window.innerHeight / - 2, 1, 1000 );
    // this.defaultCamera.position.set(0, 100, 100);
    this.activeCamera = this.defaultCamera;
    this.scene.add(this.activeCamera);
  }
  initOrbitControls() {
    this.controls = isLowIOSDevice()
      ? new OrbitControls129(this.defaultCamera, this.renderer.domElement)
      :  new OrbitControls(this.defaultCamera, this.renderer.domElement);
    this.controls.target.set(0, 0, 0);
    this.controls.enablePan = false;
    this.controls.enableDamping = true;
    this.controls.dampingFactor = 1;
    this.controls.update();
  }
  setControlDistance(cameraOpts) {
    // console.log('setControlDistance', cameraOpts);
    // window.controls = this.controls;
    // this.controls.maxDistance = this.controls.getDistance();
    this.hasControlDistanceChange = true;
    if (typeof cameraOpts.minDistance !== 'undefined') this.controls.minDistance = cameraOpts.minDistance;
    if (typeof cameraOpts.maxDistance !== 'undefined') this.controls.maxDistance = cameraOpts.maxDistance;
  }
  setPolarAngel(angle) {
    if (!angle || angle < 0) return;
    if (angle === 1) {
      this.controls.maxPolarAngle = Math.PI;
      this.controls.minPolarAngle = 0;
    } else if (angle => 0 && angle < 1) {
      this.controls.maxPolarAngle = angle * Math.PI;
      this.controls.minPolarAngle = angle * Math.PI;
    }
  }
  setAutoRotate(isAutoRotate) {
    this.controls.autoRotate = isAutoRotate;
    // 默认和线上保持一致，当于在60fps时每旋转一周需要30秒，每帧0.2°
    this.controls.autoRotateSpeed = -1;
  }

  animate() {
    requestAnimationFrame(this.animate);
    if (this.renderModel && this.renderModel.isUseFlowLight) {
      this.renderModel.updateFlowlight();
    }
    // if (this.renderModel && this.renderModel.edgeFlowLight) {
    //   this.renderModel.updateEdgeFlowLight();
    // }
    TWEEN.update();
    this.controls.update();
    this.renderer.render(this.scene, this.activeCamera);
  }
  addModel(model) {
    if (!model || !model.object) return;
    this.renderModel = model;
    this.setContent(model.object, model.clip);
  }

  updateEnv(isOpen) {
    if (this.isOpenEnv === !!isOpen) return;
    this.isOpenEnv = isOpen;
    if (!isOpen) {
      this.scene.environment = null;
    } else {
      if (this.envMap) {
        this.scene.environment = this.envMap;
        return;
      }
      // 添加环境
      const rgbeLoader = new RGBELoader();
      rgbeLoader.load('https://mapapi.qq.com/web/jsapi/assets/venice_sunset_1k.hdr', (texture) => {
        const pmremGenerator = new PMREMGenerator(this.renderer);
        pmremGenerator.compileEquirectangularShader();
        this.envMap = pmremGenerator.fromEquirectangular(texture).texture;

        this.scene.environment = this.envMap;
      });
    }
  }
  onWindowResize() {
    this.activeCamera.aspect = this.container.offsetWidth / this.container.offsetHeight;
    this.activeCamera.updateProjectionMatrix();
    this.renderer.setSize(this.container.offsetWidth, this.container.offsetHeight);
  }
  setContent(object) {
    this.clear();

    const box = new Box3().setFromObject(object);
    const size = box.getSize(new Vector3()).length();
    // const center = box.getCenter(new Vector3());
    // this.controls.reset();

    // object.position.x += (object.position.x - center.x);
    // object.position.y += (object.position.y - center.y);
    // object.position.z += (object.position.z - center.z);
    if (!this.hasControlDistanceChange) {
      this.controls.maxDistance = size * 5;
      this.controls.minDistance = size / 2;
    }
    this.activeCamera.near = size / 10;
    this.activeCamera.far = size * 5;
    // this.controls.maxDistance = size * 5;
    // this.controls.minDistance = size / 2;
    // this.activeCamera.near = size;
    // this.activeCamera.far = size * 100;

    // this.defaultCamera.position.copy(center);
    // this.defaultCamera.position.x += size / 2.0;
    // this.defaultCamera.position.y += size / 5.0;
    // this.defaultCamera.position.z += size / 2.0;
    // this.activeCamera.lookAt(center);
    // this.controls.target.set(...center);
    this.activeCamera.updateProjectionMatrix();
    // this.controls.saveState();
    this.scene.add(object);
    this.content = object;
    window.content = this.content;
  }

  // 更新lights，传入为空，不更新，仅改变传入的lights
  updateLights(lightOpt) {
    if (!lightOpt) return;
    this.removeLights();
    this.lights.forEach((light) => {
      this.addLight(light);
    });
  }

  addLights(lightOpts) {
    // type	LIGHT_TYPE	光源类型
    // id	String	（通用）光源id，默认为随机字符串
    // color	String	（通用）光色，支持rgb()、#RRGGBB形式，默认为#FFFFFF
    // intensity	Number	（通用）光强，默认为1.0
    // direction	TModel.Vector3	（方向光）光线投射方向，为一个三维向量，格式为[x, y, z]， 默认目标位置为[0,0,0]
    // position	TModel.Vector3	（点光源）光源的xyz位置
    if (!lightOpts || !isArray(lightOpts) || !lightOpts.length) return;
    lightOpts.forEach(lightOpt => this.addLight(lightOpt));
  }
  addLight(lightOpt) {
    if (!lightOpt) return;
    let lightItem = null;
    if (lightOpt.type === 'AmbientLight') {
      lightItem = new AmbientLight(lightOpt.color, lightOpt.intensity);
    } else if (lightOpt.type === 'DirectionalLight') {
      lightItem = new DirectionalLight(lightOpt.color, lightOpt.intensity);
      lightItem.position.set(...lightOpt.position);
    } else if (lightOpt.type === 'PointLight') {
      lightItem = new PointLight(lightOpt.color, lightOpt.intensity);
      lightItem.position.set(...lightOpt.position);
      this.activeCamera.add(lightItem);
    } else {
      return;
    }
    lightItem.name = lightOpt.id;
    this.scene.add(lightItem);
    this.lights.push(lightItem);
  }
  getLights() {
    return this.lights;
  }
  deleteLight(light) {
    light && light.type && this.removeLightByType(light.type);
  }
  removeLights() {
    this.lights.forEach(light => light.parent.remove(light));
    this.lights.length = 0;
  }
  removeLightByType(type) {
    for (let i = 0; i < this.lights.length; i++) {
      const lightItem = this.lights[i];
      if (lightItem.type === type) {
        lightItem.parent.remove(lightItem);
        this.lights.splice(i, 1);
        break;
      }
    }
  }
  removeLightByName(name) {
    for (let i = 0; i < this.lights.length; i++) {
      const lightItem = this.lights[i];
      if (lightItem.name === name) {
        lightItem.parent.remove(lightItem);
        this.lights.splice(i, 1);
        break;
      }
    }
  }
  updateCamera(cameraOpts) {
    if (!cameraOpts || !cameraOpts.position || !isVector3(cameraOpts.position)) return;
    // this.controls.reset();
    switch (cameraOpts.type) {
      case 'Orthographic':
        // left	Number	摄像机视锥体左侧面
        // right	Number	摄像机视锥体右侧面
        // top	Number	摄像机视锥体上侧面
        // bottom	Number	摄像机视锥体下侧面
        // near	Number	摄像机视锥体近端面
        // far	Number	摄像机视锥体远端面
        // eslint-disable-next-line no-case-declarations
        // TODO: tannerdu
        // const { left, right, top, bottom, near, far } = cameraOpts.properties;
        // newCamera = new OrthographicCamera(left, right, top, bottom, near, far);
        // console.log('orCamera', newCamera);
        break;
      case 'Perspective':
      default:
        // fov	Number	（可选）摄像机视锥体垂直视野角度，默认为60
        // aspect	Number	（可选）摄像机视锥体长宽比，默认为容器长宽比
        // near	Number	（可选）摄像机视锥体近端面
        // far	Number	（可选）摄像机视锥体远端面
        // eslint-disable-next-line no-case-declarations
        // const { fov, aspect } = cameraOpts.properties;
        if (cameraOpts.animation && cameraOpts.animation.initPosition) {
          const animationOpt = cameraOpts.animation;
          // const {end: position,
          //    'animation.initPosition' : start,
          //   type: animation.type = '',
          //   duration: animation.duration = 3
          // } = cameraOpts;
          // const start = animationOpt.initPosition;
          const currentPosition =  this.activeCamera.position;
          // console.log('start currentPosition', start, currentPosition);
          const end = cameraOpts.position;
          const type = animationOpt.type || TWEEN.Easing.Linear.None;
          const duration = animationOpt.duration || 2000;
          this.controls.maxDistance = Infinity;
          this.controls.minDistance = 0;
          TWEEN.removeAll();
          // target 动画
          if (cameraOpts.lookAt) {
            const initTarget = this.controls.target;
            const endTarget = cameraOpts.lookAt;
            // console.log('target Animation', initTarget, endTarget);
            new TWEEN.Tween([initTarget.x, initTarget.y, initTarget.z])
              .easing(type)
              .to(endTarget, duration)
              .onUpdate((object) => {
                this.controls.target.set(...object);
              })
              .start();
          }
          // position 动画
          new TWEEN.Tween([currentPosition.x, currentPosition.y, currentPosition.z])
            .easing(type)
            .to(end, duration)
            .onUpdate((object) => {
              cameraOpts.position && this.activeCamera.position.set(...object);
            })
            .onComplete(() => {
              this.setControlDistance(cameraOpts);
            })
            .start();
        } else {
          cameraOpts.lookAt && this.controls.target.set(...cameraOpts.lookAt);
          cameraOpts.position && this.activeCamera.position.set(...cameraOpts.position);
          this.setControlDistance(cameraOpts);
        }
        break;
    }
    this.activeCamera.updateMatrixWorld();
    // newCamera.position.set(cameraOpts.position);
    // newCamera.lookAt((this.content && this.content.position) || new Vector3());
    // newCamera.updateMatrixWorld();
    // this.activeCamera = newCamera;
    // this.controls.saveState();
    // this.scene.add(newCamera);
    // this.content && cameraOpts.autoNearFar && this.setContent(this.content);
  };
  setBackground(url) {
    if (this.backgroundUrl === url) return;
    this.backgroundUrl = url;
    this.scene.background = new TextureLoader().load(
      url,
      res => console.log('load texture success:', res), undefined,
      err => console.log('load texture fail:', err),
    );

    this.scene.background.encoding = sRGBEncoding;
  }
  clearBackgroundColor(isTransparent = true) {
    this.renderer.setClearColor(0xcccccc, isTransparent ? 0 : 1);
  }

  //
  // id
  // text	String	文字内容
  // position	TModel.Vector3	坐标[x, y, z]
  // rotation	TModel.Vector3	旋转[x, y, z]
  // scale	TModel.Vector3	缩放比例[x, y, z]
  // style	Object	文字参数
  addText(textOpts) {
    if (!textOpts || !textOpts.content) return;
    if (!textOpts.style) {
      textOpts.style = DEFAULT_FONT_STYLE;
    } else {
      textOpts.style = Object.assign(DEFAULT_FONT_STYLE, textOpts.style);
    }
    const loader = new FontLoader();
    const textStyle = { ...textOpts.style };
    loader.load(textStyle.font,  (font) => {
      const geometry = new TextGeometry(textOpts.content, {
        font,
        size: textStyle.fontSize,
        height: 0.1,
      });
      const mat = new MeshPhongMaterial({
        color: textStyle.color,
      });
      const mesh = new Mesh(geometry, mat);
      if (textOpts.rotation) {
        [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z] = textOpts.rotation;
      }
      mesh.position.set(...textOpts.position);

      mesh.name = textOpts.id || new Date().getTime();
      this.scene.add(mesh);
      this.texts.push(mesh);
      // this.setContent(mesh);
      // console.log(this.scene)
    });
  }
  clearText() {
    for (let i = 0; i < this.texts.length; i++) {
      const textItem = this.texts[i];
      this.scene.remove(textItem);
    }
    this.texts = [];
  }
  deleteText(id) {
    for (let i = 0; i < this.texts.length; i++) {
      const textItem = this.texts[i];
      if (textItem.name === id) {
        this.scene.remove(textItem);
        this.texts.splice(i, 1);
        break;
      }
    }
  }
  // // 重新设计流光接口
  // addAnimation(animationOptions) {
  //
  // }
  addLine(lineOptions) {
    const animate = lineOptions.animate || true;
    if (animate) {
      // 渐入线
      const { start, end } = lineOptions;
      const position = [...start];
      const tween = new TWEEN.Tween(position);
      tween.to([...end], 2000);
      tween.start();
      const geometry = this.createLines(lineOptions);
      tween.onUpdate((object) => {
        geometry.updateGeometryPoints(start, object);
      });
    } else {
      this.createLines(lineOptions);
    }
  }

  createLines(lineOptions) {
    const { color = '#ffffff', linewidth = 1, start, end, type, gapsize } = lineOptions;
    let material;
    if (type === 'dash') {
      material = new LineDashedMaterial({
        color,
        linewidth,
        dashSize: gapsize || 1,
        gapSize: gapsize || 1,
      });
    } else {
      material = new LineBasicMaterial({
        color,
        linewidth,
      });
    }
    const geometry = new BufferGeometry().setFromPoints(this.createPoints(start, end, LineNumberOfPoints));
    const line = new Line(geometry, material);
    line.computeLineDistances();
    this.lines.push(line);
    this.scene.add(line);
    return {
      updateGeometryPoints: (start, end) => {
        geometry.setFromPoints(this.createPoints(start, end, LineNumberOfPoints));
        line.computeLineDistances();
      },
    };
  }

  createPoints(start, end, count) {
    const resPoint = [];
    resPoint.push(new Vector3(...start));
    for (let i = 0; i < count; i++) {
      const point = [start[0] + (end[0] - start[0]) * i / count,
        start[1] + (end[1] - start[1])  * i / count,
        start[2] + (end[2] - start[2])  * i / count];
      resPoint.push(new Vector3(...point));
    }
    resPoint.push(new Vector3(...end));
    return resPoint;
  }

  clearLine() {
    for (let i = 0; i < this.lines.length; i++) {
      const textItem = this.lines[i];
      this.scene.remove(textItem);
    }
    this.lines = [];
  }

  /*
  * type half whole
  *
  * */
  addCircle(ringOptions) {
    const { innerRadius = 10, outerRadius = 10, color = '#ffffff', position, rotation } = ringOptions;
    const thetaLength = Math.PI * 2;
    const ringGeometry = new RingGeometry(innerRadius, outerRadius, 30, 8, 0, thetaLength);
    const material = new MeshBasicMaterial({ color, side: DoubleSide  });
    const mesh = new Mesh(ringGeometry, material);
    mesh.position.set(...position);
    mesh.rotation.set(...rotation);
    this.circles.push(mesh);
    this.scene.add(mesh);
  }
  clearCircle() {
    for (let i = 0; i < this.circles.length; i++) {
      const circleItem = this.circles[i];
      this.scene.remove(circleItem);
    }
    this.circles = [];
  }
  addRing(ringOptions) {
    const animate = ringOptions.animate || true;
    if (animate) {
      // 渐入
      const { type } = ringOptions;
      const position = [0];
      const tween = new TWEEN.Tween(position);
      tween.to([type], 2000);
      tween.start();
      const geometry = this.createRing(ringOptions);
      tween.onUpdate((object) => {
        geometry.updateGeometryPoints(object[0]);
      });
    } else {
      this.createRing(ringOptions);
    }
  }

  createRing(ringOptions) {
    const { radius = 100, color = '#ffffff', position, rotation, type = 'half', gapsize = 5 } = ringOptions;
    const dashMaterial = new LineDashedMaterial({ color, dashSize: gapsize, gapSize: gapsize  });
    const pts = new Path().absarc(0, 0, radius, 0, Number(type || 2) * Math.PI) // TODO: 想好名字后更正
      .getPoints(400);
    const circGeom = new BufferGeometry().setFromPoints(pts);
    const circ = new Line(circGeom, dashMaterial);
    circ.vertices && circ.vertices.shift();
    circ.computeLineDistances();
    circ.position.set(...position);
    circ.rotation.set(...rotation);
    this.rings.push(circ);
    this.scene.add(circ);
    return {
      updateGeometryPoints: (num) => {
        const pts = new Path().absarc(0, 0, radius, 0, Number(num || 2) * Math.PI)
          .getPoints(400); // TODO: 想好名字后更正
        circGeom.setFromPoints(pts);
        circ.computeLineDistances();
      },
    };
  }

  clearRing() {
    for (let i = 0; i < this.rings.length; i++) {
      const textItem = this.rings[i];
      this.scene.remove(textItem);
    }
    this.rings = [];
  }

  easeTo(easeToOptions, usePosition = false, callback) {
    const { target, dest, rotation, easeType, duration } = easeToOptions;
    if (!(target instanceof Object3D)) {
      return new Error('Invalid target type');
    }
    if (!isVector3(dest)) {
      return new Error('Invalid dest type');
    }
    if (!isNumber(duration)) {
      return new Error('Invalid duration type');
    }
    const easing = TWEEN.Easing; // TWEEN提供接口能力列表
    let count = 0;
    if (isString(easeType)) {
      const param = easeType.split('.');
      if (easing[param[0]][param[1]]) {
        // 执行动画时关闭自动旋转
        const checkAnimationComplete = () => {
          count += 1;
          if (count > 1) {
            // 动画执行完毕
            callback?.();
            count = 0;
          }
        };
        if (usePosition) {
          const tween = new TWEEN.Tween(target.position)
            .easing(easing[param[0]][param[1]])
            .to({ x: dest[0], y: dest[1], z: dest[2] }, duration)
            .onComplete(checkAnimationComplete);
          tween.start();
        } else {
          checkAnimationComplete();
        }
        const { startRes: startPosArr, endRes: endPosArr } = getTweenRotation(
          [target.rotation.x, target.rotation.y, target.rotation.z],
          rotation,
        );
        target.rotation.set(...startPosArr);
        const tweenRotation = new TWEEN.Tween(target.rotation)
          .easing(easing.Linear.None)
          .to({ x: endPosArr[0], y: endPosArr[1], z: endPosArr[2] }, duration)
          .onComplete(checkAnimationComplete);
        tweenRotation.start();
        return this;
      }
    }
    return new Error('Invalid match tween ease type');
  }
  getCamera() {
    return this.activeCamera;
  }
  printGraph(node) {
    console.group(` <${node.type}> ${node.name}`);
    node.children.forEach(child => this.printGraph(child));
    console.groupEnd();
  }
  clear() {
    if (!this.content) return;

    this.scene.remove(this.content);

    // dispose geometry
    this.content.traverse((node) => {
      if (!node.isMesh) return;

      node.geometry.dispose();
    });

    // dispose textures
    traverseMaterials(this.content, (material) => {
      MAP_NAMES.forEach((map) => {
        if (material[map]) material[map].dispose();
      });
    });
  }
  /**
   * @param {string} name
   */
  setCamera(name) {
    if (name === DEFAULT_CAMERA) {
      this.controls.enabled = true;
      this.activeCamera = this.defaultCamera;
    } else {
      this.controls.enabled = false;
      this.content.traverse((node) => {
        if (node.isCamera && node.name === name) {
          this.activeCamera = node;
        }
      });
    }
  }
};

function traverseMaterials(object, callback) {
  object.traverse((node) => {
    if (!node.isMesh) return;
    const materials = Array.isArray(node.material)
      ? node.material
      : [node.material];
    materials.forEach(callback);
  });
}
function getTweenRotation(startPos, endPos) {
  const startRes = [];
  const endRes = [];
  startPos.forEach((item, index) => {
    const itemRes = getTweenItemRotation(startPos[index], endPos[index]);
    startRes[index] = itemRes.start;
    endRes[index] = itemRes.end;
  });
  return { startRes, endRes };
}
// 根据输入的起始点和终点，按照最小旋转角生成对应的旋转起终点
function getTweenItemRotation(start, end) {
  start = start % (2 * Math.PI) || 0;
  end = end % (2 * Math.PI) || 0;
  const delta = (end - start) % (2 * Math.PI);
  if (delta >= Math.PI) {
    end = end - 2 * Math.PI;
  } else if (delta < - Math.PI) {
    start = start - 2 * Math.PI;
  }
  if (Math.abs((end - start) % (2 * Math.PI)) < 0.01) {
    end = start;
  }
  return { start, end };
}
